import { useQuery } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { useSearchParams } from "react-router";

import { isIsoDay, todayIn } from "../lib/dates";
import { useSession } from "./session-context";
import { useViewedUserId } from "./useViewedUser";

// One clock for every component that asks for today, ticking each minute, so
// a day view left open past midnight moves on to the new day (#240).
const listeners = new Set<() => void>();
let ticking: ReturnType<typeof setInterval> | undefined;

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  ticking ??= setInterval(() => {
    for (const notify of listeners) {
      notify();
    }
  }, 60_000);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      clearInterval(ticking);
      ticking = undefined;
    }
  };
}

/** Today in `timeZone`, kept current. */
export function useToday(timeZone: string): string {
  return useSyncExternalStore(subscribe, () => todayIn(timeZone));
}

export interface SelectedDay {
  /** The day shown; undefined until whose day it is, and so their today, is known. */
  day: string | undefined;
  /** Today in their time zone; undefined until it's known. */
  today: string | undefined;
  /** Why their time zone couldn't be known: the user list didn't load. */
  zoneError: Error | null;
  /** Read the user list again, after `zoneError`. */
  retryZone: () => void;
}

/**
 * The day the day view shows: `?date=YYYY-MM-DD`, or today in the timezone of
 * whoever's day it is (BUILD_PLAN.md section 17.1: a tech's day is in their
 * own): the signed-in user's, or, for an owner viewing someone else's, theirs,
 * from the user switcher's list (#258). A malformed date falls back to today.
 * Until that list is in, their today isn't known: it isn't guessed from the
 * owner's, which near midnight is another day (#862).
 */
export function useSelectedDay(): SelectedDay {
  const { api, me } = useSession();
  const viewed = useViewedUserId();
  const users = useQuery({
    queryKey: ["users"],
    queryFn: () => api.listUsers(),
    staleTime: 5 * 60_000,
    enabled: viewed !== null,
  });
  const known = viewed === null || users.data !== undefined;
  // Someone not on the list is left to the day API to refuse.
  const zone =
    viewed === null
      ? me.timezone
      : (users.data?.find((u) => u.id === viewed)?.timezone ?? me.timezone);
  const [searchParams] = useSearchParams();
  const today = useToday(zone);
  const requested = searchParams.get("date");
  const asked = requested !== null && isIsoDay(requested) ? requested : undefined;
  return {
    day: asked ?? (known ? today : undefined),
    today: known ? today : undefined,
    zoneError: known ? null : users.error,
    retryZone: () => {
      void users.refetch();
    },
  };
}
